import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Salad, Mail, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [demoToken, setDemoToken] = useState('');
  
  const { sendMagicLink, verifyMagicLink, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for token in URL (magic link verification)
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleVerify(token);
    }
  }, [searchParams]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const result = await sendMagicLink(email);
      setEmailSent(true);
      // Store demo token for testing (in production, remove this)
      if (result.demo_token) {
        setDemoToken(result.demo_token);
      }
      toast.success('Magic link sent! Check your email.');
    } catch (error) {
      toast.error(error.message || 'Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (token) => {
    setVerifying(true);
    try {
      await verifyMagicLink(token);
      toast.success('Successfully signed in!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Invalid or expired link');
    } finally {
      setVerifying(false);
    }
  };

  // Demo: Quick sign in with the token (for testing)
  const handleDemoSignIn = () => {
    if (demoToken) {
      handleVerify(demoToken);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-lg font-medium">Verifying your link...</p>
            <p className="text-muted-foreground mt-2">Please wait while we sign you in.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 pt-24">
      <div className="w-full max-w-md space-y-6 opacity-0 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-3 rounded-2xl bg-primary/10">
              <Salad className="h-8 w-8 text-primary" aria-hidden="true" />
            </div>
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Welcome to Gastro-Thrive
          </h1>
          <p className="text-muted-foreground mt-2">
            Sign in to start thriving
          </p>
        </div>

        <Card data-testid="auth-card">
          <CardHeader>
            <CardTitle className="text-xl">
              {emailSent ? 'Check Your Email' : 'Sign In with Email'}
            </CardTitle>
            <CardDescription>
              {emailSent 
                ? 'We sent you a magic link. Click it to sign in.'
                : 'No password needed. We\'ll send you a magic link.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {emailSent ? (
              <div className="space-y-6">
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <CheckCircle className="h-8 w-8 text-primary" aria-hidden="true" />
                  </div>
                  <p className="text-foreground font-medium">Magic link sent to:</p>
                  <p className="text-primary font-semibold mt-1">{email}</p>
                  <p className="text-sm text-muted-foreground mt-4">
                    Didn't receive it? Check your spam folder or try again.
                  </p>
                </div>

                {/* Demo quick sign in button (for testing) */}
                {demoToken && (
                  <div className="border-t pt-4">
                    <p className="text-xs text-muted-foreground text-center mb-3">
                      Demo Mode: Click below to sign in instantly
                    </p>
                    <Button 
                      onClick={handleDemoSignIn}
                      className="w-full rounded-full"
                      data-testid="demo-sign-in-btn"
                    >
                      Quick Sign In (Demo)
                    </Button>
                  </div>
                )}

                <Button 
                  variant="outline" 
                  className="w-full rounded-full"
                  onClick={() => {
                    setEmailSent(false);
                    setEmail('');
                    setDemoToken('');
                  }}
                  data-testid="try-different-email-btn"
                >
                  Try a different email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 rounded-xl"
                      required
                      aria-describedby="email-description"
                      data-testid="email-input"
                    />
                  </div>
                  <p id="email-description" className="text-xs text-muted-foreground">
                    We'll send a secure link to this email
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full rounded-full gap-2"
                  disabled={loading || !email}
                  data-testid="send-magic-link-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Magic Link
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
